import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Typography, Grid, Card, Button, Stack, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import CallToAction from '../widgets/CallToAction';
import { keyframes } from '@mui/system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatIcon from '@mui/icons-material/Chat';
import WorkIcon from '@mui/icons-material/Work';
import StarsIcon from '@mui/icons-material/Stars';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';

// Page entièrement réécrite pour mettre en avant la plateforme entreprise
export default function PresentationEntreprises() {
    // Align with student page wording style
    const humanSectionBullets = [
        "Candidatures simples et sans friction",
        "Vidéo de présentation des étudiants",
        "Échanges directs et authentiques",
        "Feedbacks clairs et actionnables",
        "Mises en relation de qualité",
        "Contrôle et confidentialité de vos données"
    ];

    // Logos & helpers (same as student page style)
    const altFromSrc = (s) => {
        try {
            const file = s.split('/').pop() || '';
            const base = file.split('.')[0] || '';
            return `Logo ${base.replace(/[-_]/g, ' ')}`;
        } catch {
            return 'Logo entreprise';
        }
    };

    const companyLogos = [
        '/assets/images/entreprises/accenture.png',
        '/assets/images/entreprises/bnp.jpg',
        '/assets/images/entreprises/Dassault.png',
        '/assets/images/entreprises/EDF.png',
        '/assets/images/entreprises/EY.png',
        '/assets/images/entreprises/puy-du-fou.png',
        '/assets/images/entreprises/toyota.png',
    ];

    const scrollX = keyframes`
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
    `;
    return (
        // Harmonized top padding with Solutions_ecoles (pt: { xs:4, md:6 })
        <Box sx={{ backgroundColor: '#fff', pt: { xs: 4, md: 6 }, pb: 10 }}>
            <Helmet>
                <title>Entreprises — Recrutement étudiants: stage, alternance, emploi | Student Link</title>
                <meta name="description" content="Publiez des offres, échangez avec étudiants et écoles, valorisez votre marque employeur et sourcez via CVthèque." />
                <link rel="canonical" href="https://studentlink.fr/entreprises" />
            </Helmet>
            {/* HERO */}
            <Container maxWidth="lg">
                {/* Added bottom margin to hero section to match Solutions_ecoles spacing (mb: { xs:8, md:10 }) */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: { xs: 8, md: 10 } }}>
                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="h2"
                            component="h1"
                            sx={{ fontWeight: 700, lineHeight: 1.1, mt: 1, fontSize: { xs: 42, md: 56 } }}
                        >
                            Recrutez. Collaborez. Rayonnez.
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary', fontWeight: 400, maxWidth: 640 }}>
                            Publiez des offres sans limite, gérez vos candidatures, échangez avec étudiants, associations & écoles,
                            valorisez votre marque employeur et animez vos partenariats en un seul endroit.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                            <Button
                                component="a"
                                href="https://plateforme-entreprise.studentlink.fr/"
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="contained"
                                sx={{ height: 48, px: 3, borderRadius: 4, fontWeight: 700 }}
                            >
                                Connexion/Inscription
                            </Button>
                            <Button
                                component="a"
                                href="https://calendly.com/j-deservigny-studentlink/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outlined"
                                sx={{ height: 48, px: 3, borderRadius: 4, fontWeight: 600 }}
                            >
                                Demander une démo
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* Placeholder visuel aligné avec la page étudiant */}
                        <Box sx={{
                            position: 'relative',
                            borderRadius: 8,
                            height: { xs: 300, md: 420 },
                            // background: 'linear-gradient(135deg,#0075FF 0%,#00B2FF 100%)',
                            // boxShadow: '0 10px 35px rgba(0,117,255,0.25)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden'
                        }}>
                            <Box
                                component="img"
                                src="/assets/images/plateforme_company/placeholder.jpg"
                                alt="Interface entreprise Student Link"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'left center',
                                    borderRadius: 8,
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* BENEFITS GRID */}
            <Container maxWidth="lg" sx={{ mt: 10 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center', mb: 4, fontSize: { xs: 34, md: 44 } }}>
                    Un véritable "game changer"
                </Typography>
                <Grid container spacing={4}>

                    {[
                        {
                            icon: <WorkIcon sx={{ fontSize: 40, color: 'grey.800' }} />,
                            title: 'Offres d\'emploi illimitées',
                            text: 'CDI, CDD, stage, alternance : publiez sans limite et gratuitement. Aucune barrière de volume.'
                        },
                        {
                            icon: <StarsIcon sx={{ fontSize: 40, color: 'grey.800' }} />,
                            title: 'Marque employeur valorisée',
                            text: 'Page dédiée, storytelling, médias enrichis et mises en avant sur les parcours étudiants.'
                        },
                        {
                            icon: <ChatIcon sx={{ fontSize: 40, color: 'grey.800' }} />,
                            title: 'Chat temps réel',
                            text: 'Conversations fluides avec étudiants, associations et écoles dans un seul fil unifié.'
                        },
                        {
                            icon: <PeopleIcon sx={{ fontSize: 40, color: 'grey.800' }} />,
                            title: 'Partenaires & Relations Campus',
                            text: 'Gérez vos liens avec associations étudiantes et écoles, centralisez les contacts & actions.'
                        },
                        {
                            icon: <EventIcon sx={{ fontSize: 40, color: 'grey.800' }} />,
                            title: 'Événements étudiants',
                            text: 'Participez ou sponsorisez forums, hackathons, conférences, job datings et plus encore.'
                        },
                        {
                            icon: <CheckCircleIcon sx={{ fontSize: 40, color: 'grey.800' }} />,
                            title: 'Sourcing via CVthèque',
                            text: 'Interface claire, onboarding express, aucune courbe d\'apprentissage lourde.'
                        }
                    ].map(card => (
                        <Grid item xs={12} sm={6} md={4} key={card.title}>
                            <Card sx={{ height: '100%', p: 2.5, borderRadius: 8, border: '1px solid', borderColor: 'divider', boxShadow: 'none', transition: 'box-shadow .2s ease', '&:hover': { boxShadow: '0 6px 18px rgba(0,0,0,0.06)' } }}>
                                <Stack spacing={2} alignItems="center">
                                    {card.icon}
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center' }}>{card.title}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>{card.text}</Typography>
                                </Stack>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* LOGOS MARQUEE (aligné sur la page étudiant) */}
            <Container maxWidth="lg" sx={{ mt: 10 }}>
                <Box sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    py: { xs: 2, md: 3 },
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: 'max-content',
                            gap: { xs: 6, md: 8 },
                            animation: `${scrollX} linear infinite`,
                            animationDuration: { xs: '50s', md: '40s' },
                            '@media (prefers-reduced-motion: reduce)': {
                                animation: 'none'
                            },
                        }}
                    >
                        {[...companyLogos, ...companyLogos].map((src, i) => (
                            <Box
                                key={`logo-${i}`}
                                component="img"
                                src={src}
                                alt={altFromSrc(src)}
                                loading="lazy"
                                decoding="async"
                                sx={{
                                    height: { xs: 28, sm: 36, md: 44 },
                                    width: 'auto',
                                    filter: 'grayscale(100%)',
                                    opacity: 0.85,
                                    transition: 'all .2s ease',
                                    '&:hover': { filter: 'none', opacity: 1 }
                                }}
                            />
                        ))}
                    </Box>
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        width: 64,
                        pointerEvents: 'none',
                        background: 'linear-gradient(90deg, #ffffff 0%, rgba(255,255,255,0) 100%)'
                    }} />
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        width: 64,
                        pointerEvents: 'none',
                        background: 'linear-gradient(270deg, #ffffff 0%, rgba(255,255,255,0) 100%)'
                    }} />
                </Box>
            </Container>

            {/* HUMAIN AU CENTRE */}
            <Container maxWidth="lg" sx={{ mt: 10 }}>
                <Grid container spacing={4} alignItems="stretch">
                    {/* Colonne gauche: image handshake */}
                    <Grid item xs={12} md={6}>
                        <Box
                            component="img"
                            src="/assets/images/handshake.jpg"
                            alt="Poignée de main — collaboration"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = '/assets/images/team.jpg';
                            }}
                            sx={{
                                width: '100%',
                                height: { xs: 240, md: 420 },
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        />
                    </Grid>
                    {/* Colonne droite: contenu texte sans boîte */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex', minHeight: { xs: 240, md: 420 } }}>
                        <Box sx={{ p: { xs: 0, md: 1 }, height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography variant="h3" sx={{ fontWeight: 700, mt: 1, fontSize: { xs: 30, md: 40 } }}>L’humain avant tout</Typography>
                            <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                                Nous proposons directement vos offres au bon candidats et facilitons les échanges pour que vous puissiez recruter le meilleur partenaire de danse.
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 3 }}>
                                {humanSectionBullets.map(b => (
                                    <Grid item xs={12} sm={6} key={b}>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <Box sx={{ fontSize: 18, lineHeight: '22px' }}>✅</Box>
                                            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{b}</Typography>
                                        </Stack>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* STATS ROW */}
            <Container maxWidth="lg" sx={{ mt: 10 }}>
                <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                    {[
                        { value: '+10k', label: 'Étudiants actifs' },
                        { value: '+1k', label: 'Entreprises' },
                        { value: '+50', label: 'Écoles et Associations' },
                        { value: '+100k', label: 'Offres analysées / mois' },
                    ].map((s) => (
                        <Grid item xs={6} md={3} key={s.label}>
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    p: { xs: 2, md: 3 },
                                    borderRadius: 8,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    height: '100%',
                                }}
                            >
                                <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1 }}>
                                    {s.value}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {s.label}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>


            {/* FINAL CTA */}
            <Container maxWidth="lg" sx={{ mt: 10 }}>
                <CallToAction
                    title="Lancez-vous aujourd'hui. C'est 100% gratuit."
                    subtitle="Pas d'engagement. Pas de limite d'offres. Un vrai gain de temps pour vos équipes."
                    actions={(
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                            <Button component="a" href="https://plateforme-entreprise.studentlink.fr/" target="_blank" rel="noopener noreferrer" variant="contained" sx={{ height: 48, borderRadius: 4, px: 5, fontWeight: 700 }}>Connexion/Inscription</Button>
                            <Button component="a" href="https://calendly.com/j-deservigny-studentlink/30min" target="_blank" rel="noopener noreferrer" variant="outlined" sx={{ height: 48, borderRadius: 4, px: 5, fontWeight: 600 }}>Réserver une démo</Button>
                        </Stack>
                    )}
                />
            </Container>
        </Box>
    );
}